// iter87.199.178 — 4-level taxonomy redesign (Sport → Style → League/
// Conference → Details), replacing the old flat "sport tiles + big
// nested-dropdown cards" layout. Shared by pages/TournamentSetup.jsx
// (the "+ New Tournament" screen — the only real consumer today).
//
// Progressive Vertical Reveal: picking Sport reveals Style pills;
// picking a Style reveals the League/Conference choices (skipped
// automatically when there's only one possible league — e.g. NFL
// Season Pot Pick'em or a golf event with a single curated binding);
// resolving the league reveals Details (season window / picks-per-
// week / format). The Matchday Boost + Weekly Spread Challenge
// checkboxes are gone entirely — both default ON server-side, with a
// static notice instead of a toggle. Scoring specifics move into an
// info popover next to the Style row instead of long inline prose.
//
// iter87.592 (user-requested) — collapsed the redundant "Details"
// layer for formats that end at League/Style with no further real
// choice: NFL/CFB Playoffs, NWSL/MLS/UCL Playoffs, Soccer Club
// Season, NFL Season Pot Pick'em, and Golf with exactly one curated
// event. Per-league scoring differs within some of these (MLS ≠ UCL
// ≠ NWSL; NFL ≠ CFB), so a single shared info popover at the League
// header would either show stale/wrong text or force one combined
// wall of text. Fix: each League/Style TILE that has its own distinct
// scoring now carries its OWN small (i) icon right next to it — only
// that tile's rules ever render, never combined. Formats where the
// Details step still requires real input (Squares' game picker, Golf
// with 2+ events, Weekly Pick'em's picks-per-round, World Cup's
// extra Format step) are untouched.
import { useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
    SPORTS, SOCCER_TOURNAMENTS, CLUB_LEAGUES, CLUB_LEAGUES_HALVES, GOLF_TYPES, NFL_TYPES, FOOTBALL_PLAYOFFS_TYPES,
    FOOTBALL_SQUARES_TYPES, SQUARES_MODES, SOCCER_BOOST_TYPES,
} from "./constants";
import { SPORT_META, SportHeaderIcon, SPORT_TEXT_CLASS } from "./SportIcon";

const rawLeagueKey = (k) => ((k || "").startsWith("pickem:") ? k.slice(7) : k);
const isSoccerTournamentTpl = (k) => SOCCER_TOURNAMENTS.some((t) => t.types.some((ty) => ty.key === k));
const isGolfTpl = (k) => (k || "").startsWith("golf_");
const isNflTpl = (k) => (k || "").includes("nfl_season") || k === "nfl_boost_api";
const isFootballPlayoffsTpl = (k) => k === "nfl_playoffs_api" || k === "cfb_playoffs_api";
const isFootballSquaresTpl = (k) => FOOTBALL_SQUARES_TYPES.some((t) => t.key === k);
const isNwslPlayoffsTpl = (k) => k === "nwsl_playoffs_api";
const isMlsPlayoffsTpl = (k) => k === "mls_playoffs_api";
const isUclPlayoffsTpl = (k) => k === "ucl_playoffs_api";
const isSoccerBoostTpl = (k) => SOCCER_BOOST_TYPES.some((s) => s.key === k);
const isClubTpl = (k) =>
    (!isNflTpl(k) && !isSoccerBoostTpl(k) && (k || "").startsWith("pickem:")) || (k || "").startsWith("dyn:") ||
    CLUB_LEAGUES.some((l) => l.key === k);
const sportForTemplate = (k) => {
    if (!k) return null;
    if (k === "custom") return "custom";
    if (k === "mlb_playoffs_api") return "baseball";
    if (k === "nba_playoffs_api") return "basketball";
    if (isNwslPlayoffsTpl(k)) return "soccer";
    if (isMlsPlayoffsTpl(k)) return "soccer";
    if (isUclPlayoffsTpl(k)) return "soccer";
    if (isGolfTpl(k)) return "golf";
    if (isNflTpl(k) || isFootballPlayoffsTpl(k)) return "football";
    if (isFootballSquaresTpl(k)) return "squares";
    if (isSoccerBoostTpl(k) || isClubTpl(k) || isSoccerTournamentTpl(k)) return "soccer";
    return null;
};
// Step-2 "Style" derivation from a committed template key.
const styleForTemplate = (k) => {
    if (!k) return null;
    if (isGolfTpl(k)) return k;
    if (isNwslPlayoffsTpl(k)) return "playoffs";
    if (isMlsPlayoffsTpl(k)) return "playoffs";
    if (isSoccerTournamentTpl(k)) return "tournament";
    if (isSoccerBoostTpl(k) || k === "nfl_boost_api") return "weekly";
    if (isClubTpl(k)) return "club";
    if (k === "pickem:nfl_season_api") return "pot";
    if (isFootballPlayoffsTpl(k)) return "playoffs";
    if (isFootballSquaresTpl(k)) return FOOTBALL_SQUARES_TYPES.find((t) => t.key === k)?.mode || null;
    return null;
};

function StepHeader({ n, label }) {
    return (
        <div className="flex items-center gap-2 mb-2.5">
            <span className="w-5 h-5 rounded-full bg-white/[0.06] border border-white/[0.12] text-[10px] font-black flex items-center justify-center text-zinc-400 shrink-0">{n}</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</span>
        </div>
    );
}

function ScoringInfo({ text }) {
    if (!text) return null;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="wizard-scoring-info-btn"
                    aria-label="View scoring rules"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/20 text-zinc-500 hover:text-cyber hover:border-cyber/50 shrink-0 transition-colors"
                >
                    <Info className="w-2.5 h-2.5" strokeWidth={2.5} />
                </button>
            </PopoverTrigger>
            <PopoverContent
                onClick={(e) => e.stopPropagation()}
                data-testid="wizard-scoring-info-popover"
                align="start"
                collisionPadding={12}
                className="bg-[#171717] border-white/10 text-zinc-300 text-[11px] leading-relaxed w-72 shadow-xl"
            >
                {text}
            </PopoverContent>
        </Popover>
    );
}

function BoostNotice({ label = "Boosts" }) {
    return (
        <p data-testid="wizard-boost-default-notice" className="text-[11px] text-zinc-500 leading-snug border-l-2 border-volt/40 pl-2.5 mt-3">
            <span className="text-volt font-bold">⚡ {label} {label === "Boosts" ? "are" : "is"} ON by default</span> — change anytime in Tournament Settings after creation.
        </p>
    );
}

export default function TemplatePicker({
    template, setTemplate,
    seasonWindow, setSeasonWindow,
    golfBindingId, setGolfBindingId,
    squaresGames = [], squaresGamesLoading = false, squaresGameId = null, setSquaresGameId = () => {},
    visibilityOverrides = {}, disabledTemplates = [],
    clubBindings = null, golfBindings = null,
    compact = false,
    nflPicksPerRound = 8, setNflPicksPerRound = () => {},
    customEnabled = true,
    proPlusOk = false,
}) {
    // Site-Admin visibility resolution: hidden | coming_soon | closed | live.
    const stateFor = (key, dflt = true) => {
        const override = visibilityOverrides[key];
        if (override === "hidden") return "hidden";
        if (override === "coming_soon") return "coming_soon";
        if (override === "closed") return "closed";
        if (override === "live") return "live";
        return dflt ? "live" : "coming_soon";
    };
    const liveKey = (key) => stateFor(key) === "live" && !disabledTemplates.includes(key);
    // Unified 4-state resolver: an explicit "coming_soon"/"closed"
    // override wins outright; the legacy boolean disabledTemplates list
    // (no explicit override set) degrades to "coming_soon" for
    // badge/sort purposes, preserving its old behavior.
    const resolvedState = (key, dflt = true) => {
        const st = stateFor(key, dflt);
        if (st !== "live") return st;
        return disabledTemplates.includes(key) ? "coming_soon" : "live";
    };
    const STATE_ORDER = { live: 0, coming_soon: 1, closed: 2 };
    const byState = (a, b) => (STATE_ORDER[a.state] ?? 0) - (STATE_ORDER[b.state] ?? 0);
    const stateSuffix = (state) => (state === "closed" ? " (Closed)" : state === "coming_soon" ? " (Soon)" : "");

    const [sport, setSport] = useState(() => {
        const s = sportForTemplate(template) || "soccer";
        if (s === "custom" && !customEnabled) return "soccer";
        if (s === "squares" && !proPlusOk) return "soccer";
        return s;
    });
    const [style, setStyle] = useState(() => styleForTemplate(template));

    // ---- Soccer · Tournament ------------------------------------------
    const wcTournaments = SOCCER_TOURNAMENTS
        .map((t) => {
            const st = t.comingSoon ? "coming_soon" : resolvedState(t.key, true);
            return { ...t, state: st, types: t.types.filter((ty) => liveKey(ty.key)) };
        })
        .filter((t) => t.state !== "hidden")
        .filter((t) => t.state !== "live" || t.types.length > 0)
        .sort(byState);
    const liveWcTournaments = wcTournaments.filter((t) => t.state === "live" && t.types.length > 0);
    const [wcKey, setWcKey] = useState(liveWcTournaments[0]?.key);
    const wcEntry = liveWcTournaments.find((t) => t.key === wcKey) || liveWcTournaments[0];
    const [wcType, setWcType] = useState(
        isSoccerTournamentTpl(template) ? template : wcEntry?.types[0]?.key,
    );
    const wcTypeEntry = wcEntry?.types.find((ty) => ty.key === wcType) || wcEntry?.types[0];

    // ---- Soccer · Club Season -------------------------------------------
    // iter87.199.178 — "Auto Draft" mode was already unreachable
    // (`_clubModesFor` filtered it out site-wide since Super Draft's
    // retirement) — collapsed the dead mode dropdown; Pot Pick'em is
    // simply the only club-season mode now.
    const builtinLeagues = CLUB_LEAGUES
        .map((l) => {
            const st = resolvedState(l.key);
            if (st === "hidden") return null;
            return { ...l, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);
    const dynLeagues = (clubBindings || [])
        .filter((b) => b.league_id && !CLUB_LEAGUES.some((l) => l.key === b.event_id))
        .map((b) => ({
            key: `dyn:${b.id}`, label: b.label, state: "live",
            rounds: b.rounds || (b.team_seeds ? (Object.keys(b.team_seeds).length - 1) * 2 : 38),
        }));
    // iter87.234.5 (operator ask) — PL/La Liga 1H/2H tiles each get their
    // OWN visibility key now (`premier_league_api__1h` etc.) so Product
    // Ops can expose e.g. only the 2nd Half without also exposing the
    // 1st Half for the same league. Was gated on the shared underlying
    // template key before, which made that impossible.
    const clubHalfLeagues = CLUB_LEAGUES_HALVES
        .map((l) => {
            const st = resolvedState(l.key);
            if (st === "hidden") return null;
            return { ...l, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);
    const clubSeasonState =
        (builtinLeagues.length === 0 && dynLeagues.length === 0 && clubHalfLeagues.length === 0) ? "hidden"
        : (builtinLeagues.every((l) => l.disabled) && dynLeagues.length === 0 && clubHalfLeagues.every((l) => l.disabled)) ? "coming_soon"
        : "live";
    const clubLeagues = [...builtinLeagues, ...dynLeagues, ...clubHalfLeagues].sort(byState);
    const [clubLeague, setClubLeague] = useState(() => {
        const raw = rawLeagueKey(isClubTpl(template) ? template : "");
        return raw || builtinLeagues.find((l) => !l.disabled)?.key || dynLeagues[0]?.key || clubHalfLeagues.find((l) => !l.disabled)?.key;
    });
    const clubLeagueEntry = clubLeagues.find((l) => l.key === clubLeague) || clubLeagues[0];
    const clubTemplateFor = (leagueKey) => `pickem:${leagueKey}`;
    const rounds = clubLeagueEntry?.rounds || 38;
    const half = Math.ceil(rounds / 2);
    const clubDesc = "No draft — everyone picks clubs from tiered pots (overlap allowed). W/D/L points all season; Power Pick multiplies by pot (1×/1.5×/2×).";
    // The active half entry (1st or 2nd) if the picked league is one of
    // the PL/La Liga half tiles — undefined for UCL / dynamic leagues,
    // which are still plain full-season, single-choice picks.
    const activeHalf = clubHalfLeagues.find((l) => l.key === clubLeague);

    // ---- Golf --------------------------------------------------------
    const golfTypes = GOLF_TYPES
        .map((g) => {
            const st = g.comingSoon ? "coming_soon" : resolvedState(g.key);
            if (st === "hidden") return null;
            return { ...g, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);
    const golfTypeEntry = golfTypes.find((g) => g.key === style) || golfTypes.find((g) => g.key === template);
    const golfEventOptions = (golfBindings || []).filter((b) => b?.id && b?.label);
    const golfMultiEvent = golfEventOptions.length > 1;

    // ---- Football · NFL / College -------------------------------------
    const nflTypes = NFL_TYPES
        .map((n) => {
            const st = n.comingSoon ? "coming_soon" : resolvedState(n.visKey);
            if (st === "hidden") return null;
            return { ...n, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);
    const nflWeeklyEntry = nflTypes.find((n) => n.key === "nfl_boost_api");
    const nflPotEntry = nflTypes.find((n) => n.key === "pickem:nfl_season_api");
    // Football → Weekly Pick'em League list — NFL only today.
    const footballWeeklyLeagues = nflWeeklyEntry
        ? [{ key: "nfl_boost_api", label: "NFL", disabled: nflWeeklyEntry.disabled, state: nflWeeklyEntry.state }]
        : [];
    const footballWeeklySelected = template === "nfl_boost_api" ? "nfl_boost_api" : null;

    // ---- Football · Playoffs (NFL / CFB) ------------------------------
    const footballPlayoffsTypes = FOOTBALL_PLAYOFFS_TYPES
        .map((n) => {
            const st = resolvedState(n.visKey);
            if (st === "hidden") return null;
            return { ...n, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);

    // ---- Squares (NFL / CFB) — iter87.548 → iter87.553 (promoted to a
    // top-level sport; filtered by Step-2 mode in squaresLeagueTypes) --
    const footballSquaresTypes = FOOTBALL_SQUARES_TYPES
        .map((n) => {
            const st = resolvedState(n.visKey);
            if (st === "hidden") return null;
            return { ...n, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);
    const squaresLeagueTypes = footballSquaresTypes.filter((n) => n.mode === style);

    // ---- Soccer · Weekly Pick'em (EPL/La Liga) ------------------------
    const soccerBoostTypes = SOCCER_BOOST_TYPES
        .map((n) => {
            const st = resolvedState(n.key);
            if (st === "hidden") return null;
            return { ...n, state: st, disabled: st !== "live" };
        })
        .filter(Boolean)
        .sort(byState);

    // ---- Soccer · Playoffs (NWSL) — iter87.362. Single-league card,
    // same "no real choice" auto-resolve shape as Football → Pot
    // (NFL Season Pot Pick'em).
    const nwslState = resolvedState("nwsl_playoffs_api");
    // iter87.465.20 — MLS Playoffs, second tile in the same Soccer →
    // Playoffs League step.
    const mlsState = resolvedState("mls_playoffs_api");
    const uclState = resolvedState("ucl_playoffs_api");
    // iter87.592 — per-league scoring text, now shown via a small (i)
    // icon next to EACH tile instead of one shared Details popover.
    const nwslPlayoffsDesc = "Pure pick'em — no draft, no rostered teams. Champions Pick (any of the 8 bracket teams, +10) and Dark Horse Pick (seed 5-8 only, +5 per win) both lock at Round 1 kickoff. Playoff Predictor per game: correct winner +3, how it's decided (Regulation/Extra Time/Penalties) +2, total goals over/under 2.5 +5.";
    const mlsPlayoffsDesc = "Pure pick'em — no draft, no rostered teams. MLS Champion (1-2 picks, +15/+5 hedge), Dark Horse Pick (seed 6-9, +5 per win), and Play-In Bonus (1 pick per conference, +6 correct / -2 incorrect) all lock at Wild Card kickoff. Round One (best-of-3) is the Series Predictor: +6 correct winner, +10 with exact deciding game, -2 wrong winner. Conf Semis/Finals/MLS Cup reuse the Playoff Predictor (Asian handicap + over/under per game).";
    const uclPlayoffsDesc = "Pure pick'em — no draft, no rostered teams. UCL Champion (1-2 picks, +20/+8 hedge) and Dark Horse Pick (seed 9-24 only — seed 17-24 earns +10 per tie advanced, seed 9-16 earns +5) both lock at Knockout Round Play-offs kickoff. Two-Leg Predictor per tie: Knockout/Round of 16 +3 correct / -1 incorrect, Quarter/Semi-finals +6 / -2. The Final is the Champions Predictor: winner +10, decided by Regulation/Extra Time/Penalties +5, total goals over/under 2.5 +5.";

    // ---- Step-2 Style option lists per sport --------------------------
    const soccerStyles = [
        { key: "tournament", label: "Tournament", available: liveWcTournaments.length > 0 },
        { key: "club", label: "Club Season", available: clubSeasonState !== "hidden" },
        { key: "weekly", label: "Weekly Pick'em", available: soccerBoostTypes.some((n) => !n.disabled) },
        { key: "playoffs", label: "Playoffs", available: nwslState === "live" || mlsState === "live" },
    ].filter((s) => s.available);
    const footballStyles = [
        { key: "weekly", label: "Weekly Pick'em", available: footballWeeklyLeagues.some((l) => !l.disabled) },
        { key: "pot", label: "Season Pot Pick'em", available: !!nflPotEntry && !nflPotEntry.disabled },
        { key: "playoffs", label: "Playoffs", available: footballPlayoffsTypes.some((n) => !n.disabled) },
    ].filter((s) => s.available);

    const selectedCard = template === "custom" ? "custom"
        : isGolfTpl(template) ? "golf"
        : isFootballPlayoffsTpl(template) ? "footballPlayoffs"
        : isFootballSquaresTpl(template) ? "squares"
        : template === "nfl_boost_api" ? "footballWeekly"
        : template === "pickem:nfl_season_api" ? "footballPot"
        : isSoccerBoostTpl(template) ? "soccerWeekly"
        : isClubTpl(template) ? "club"
        : isSoccerTournamentTpl(template) ? "tournament"
        : isNwslPlayoffsTpl(template) ? "soccerPlayoffs"
        : isMlsPlayoffsTpl(template) ? "soccerPlayoffs"
        : isUclPlayoffsTpl(template) ? "soccerPlayoffs"
        : null;

    // ---- Commit handlers -----------------------------------------------
    const pickTournamentType = (typeKey) => { if (typeKey) setTemplate(typeKey); };
    const pickClubLeague = (leagueKey) => {
        if (!leagueKey) return;
        // iter87.233 — 1st/2nd-Half tile: commit the underlying template
        // but force season_window so live-standings pots kick in for 2H.
        const halfEntry = CLUB_LEAGUES_HALVES.find((l) => l.key === leagueKey);
        if (halfEntry) {
            setTemplate(clubTemplateFor(halfEntry.template));
            setSeasonWindow(halfEntry.window);
            return;
        }
        setTemplate(clubTemplateFor(leagueKey));
        // Reset — otherwise a prior 2nd-Half pick leaks into a fresh
        // full-season league selection.
        setSeasonWindow("full");
    };
    const pickGolf = (typeKey, eventId) => {
        if (!typeKey) return;
        if (eventId !== undefined) setGolfBindingId(eventId || null);
        setTemplate(typeKey);
    };
    const pickFootballWeekly = (leagueKey) => {
        if (!leagueKey) return;
        if (leagueKey === "nfl_boost_api") setTemplate("nfl_boost_api");
    };
    const pickFootballPlayoffs = (typeKey) => { if (typeKey) setTemplate(typeKey); };
    const pickSquaresLeague = (typeKey) => { if (typeKey) setTemplate(typeKey); };
    const pickSoccerWeekly = (leagueKey) => { if (leagueKey) setTemplate(leagueKey); };

    const handleSportPick = (key) => {
        setSport(key);
        setStyle(null);
        if (key === "custom") { setTemplate("custom"); return; }
        if (key === "baseball") { setTemplate("mlb_playoffs_api"); return; }
        if (key === "basketball") { setTemplate("nba_playoffs_api"); return; }
        if (sportForTemplate(template) !== key) setTemplate("");
    };

    const handleStylePick = (newStyle) => {
        if (styleForTemplate(template) !== newStyle) setTemplate("");
        setStyle(newStyle);
        // Auto-resolve when the Style already fully disambiguates the
        // template (no real League/Conference choice to make).
        if (sport === "football" && newStyle === "pot" && nflPotEntry && !nflPotEntry.disabled) {
            setTemplate("pickem:nfl_season_api");
        }
        if (sport === "golf" && !golfMultiEvent) {
            const entry = golfTypes.find((g) => g.key === newStyle);
            if (entry && !entry.disabled) pickGolf(newStyle, golfBindingId);
        }
        if (sport === "soccer" && newStyle === "playoffs" && nwslState === "live" && mlsState !== "live") {
            setTemplate("nwsl_playoffs_api");
        }
        // iter87.465.20 — auto-resolve to MLS only when it's the lone
        // live option (same "skip the no-op pick" rule as NWSL above).
        if (sport === "soccer" && newStyle === "playoffs" && mlsState === "live" && nwslState !== "live") {
            setTemplate("mls_playoffs_api");
        }
    };

    const pillCls = (selected, disabled = false) =>
        `px-3.5 py-2 rounded-xl border text-[12px] font-bold transition-all active:scale-[0.98] ${
            disabled
                ? "border-white/[0.04] bg-white/[0.02] text-zinc-600 opacity-40 cursor-not-allowed"
                : selected
                    ? "border-cyber/60 bg-cyber/[0.10] text-cyber shadow-[0_0_16px_-6px_rgba(212,246,68,0.5)]"
                    : "border-white/[0.08] bg-[#141414] text-zinc-300 hover:bg-[#181818] hover:border-white/[0.16] cursor-pointer"
        }`;
    const selCls = `brutal-input w-full text-sm ${compact ? "py-1 text-[12px]" : "py-1.5"}`;
    const sectionCls = "rounded-2xl border border-white/[0.08] bg-[#141414] p-4";

    return (
        <div className="space-y-4" data-testid="template-picker">
            {/* ---- Step 1: Sport ---- */}
            <div>
                <StepHeader n={1} label="Select sport" />
                {(() => {
                    // iter87.228 (user-requested cleanup) — live sports get
                    // their own row; anything Coming Soon (site-admin
                    // override or template not shipped yet) is grouped into
                    // a visually separate section below, instead of both
                    // interleaved in one grid based on SPORTS array order.
                    const visible = SPORTS.filter((s) => {
                        const override = visibilityOverrides[s.key];
                        if (override === "hidden") return false;
                        if (s.key === "custom" && !customEnabled) return false;
                        // iter87.573 (user-requested) — Squares should be
                        // hidden entirely for free accounts, not shown
                        // disabled with a Pro+ badge.
                        if (s.proPlusOnly && !proPlusOk) return false;
                        return true;
                    });
                    const live = visible.filter((s) => stateFor(s.key, s.available) === "live");
                    const soon = visible.filter((s) => stateFor(s.key, s.available) === "coming_soon");
                    const closed = visible.filter((s) => stateFor(s.key, s.available) === "closed");
                    const renderTile = (s) => {
                        const override = visibilityOverrides[s.key];
                        const st = stateFor(s.key, s.available);
                        const proGated = s.proPlusOnly && !proPlusOk;
                        const disabled = st !== "live" || proGated;
                        const badge = override === "closed" ? "Closed" : proGated ? "Pro+" : (override === "coming_soon" || (disabled && s.badge)) ? "Soon" : null;
                        const selected = sport === s.key;
                        return (
                            <button
                                key={s.key}
                                type="button"
                                data-testid={`sport-${s.key}-btn`}
                                data-coming-soon={disabled ? "1" : undefined}
                                onClick={() => { if (!disabled) handleSportPick(s.key); }}
                                disabled={disabled}
                                title={disabled ? (proGated ? "Pro+/comp rivalries only — upgrade to unlock Squares" : st === "closed" ? "Season closed — check back later" : "Coming soon — check back later") : s.desc}
                                className={`relative flex flex-col items-center justify-center gap-0.5 p-2 rounded-2xl border transition-all active:scale-[0.98] ${
                                    disabled
                                        ? "border-white/[0.04] bg-white/[0.02] opacity-40 cursor-not-allowed"
                                        : selected
                                            ? "border-cyber/60 bg-cyber/[0.08] shadow-[0_0_18px_-8px_rgba(212,246,68,0.5)]"
                                            : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-white/[0.16]"
                                }`}
                            >
                                <span className={`text-lg leading-none ${!disabled && SPORT_META[s.key] ? SPORT_TEXT_CLASS[SPORT_META[s.key].accent] : ""}`}>
                                    {SPORT_META[s.key] ? <SportHeaderIcon sport={s.key} className="w-5 h-5" /> : s.icon}
                                </span>
                                <span className="font-bold text-[11px] leading-tight">{s.label}</span>
                                {badge && (
                                    <span data-testid={`sport-${s.key}-badge`} className="absolute -top-1.5 -right-1 pill bg-white/[0.03] text-zinc-400 text-[8px] py-0 px-1">
                                        {badge}
                                    </span>
                                )}
                            </button>
                        );
                    };
                    return (
                        <>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5" data-testid="sport-tiles-live">
                                {live.map(renderTile)}
                            </div>
                            {soon.length > 0 && (
                                <div className="mt-3" data-testid="sport-tiles-coming-soon">
                                    <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">Coming soon</p>
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                                        {soon.map(renderTile)}
                                    </div>
                                </div>
                            )}
                            {closed.length > 0 && (
                                <div className="mt-3" data-testid="sport-tiles-closed">
                                    <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">Closed</p>
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                                        {closed.map(renderTile)}
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* ---- Step 2: Style (Soccer / Football) ---- */}
            {(sport === "soccer" || sport === "football") && (
                <div className={sectionCls} data-testid="step-style">
                    <StepHeader n={2} label="Style / Game type" />
                    <div className="flex flex-wrap gap-2">
                        {(sport === "soccer" ? soccerStyles : footballStyles).map((s) => (
                            <div key={s.key} className="inline-flex items-center gap-1">
                                <button
                                    type="button"
                                    data-testid={`style-${s.key}-btn`}
                                    onClick={() => handleStylePick(s.key)}
                                    className={pillCls(style === s.key)}
                                >
                                    {s.label}{s.proPlusOnly && !proPlusOk ? " (Pro+)" : ""}
                                </button>
                                {/* iter87.592 — NFL Season Pot Pick'em auto-resolves to its
                                    one live league with no further League step, so its info
                                    icon lives on the Style pill itself. */}
                                {sport === "football" && s.key === "pot" && <ScoringInfo text={nflPotEntry?.desc} />}
                            </div>
                        ))}
                    </div>

                    {/* ---- Step 3: League / Conference ---- */}
                    {sport === "soccer" && style === "club" && clubSeasonState === "coming_soon" && (
                        <p className="text-[11px] text-zinc-500 mt-3" data-testid="template-club-season-coming-soon">Coming soon — check back later.</p>
                    )}
                    {sport === "soccer" && style === "club" && clubSeasonState === "live" && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <div className="flex items-center gap-1.5 mb-2.5">
                                <StepHeader n={3} label="League" />
                                <ScoringInfo text={clubDesc} />
                            </div>
                            <div className="flex flex-wrap gap-2 -mt-2.5" data-testid="club-season-league-options">
                                {clubLeagues.map((l) => (
                                    <button
                                        key={l.key}
                                        type="button"
                                        data-testid={`club-league-${l.key}-btn`}
                                        disabled={l.disabled}
                                        onClick={() => { setClubLeague(l.key); pickClubLeague(l.key); }}
                                        className={pillCls(clubLeague === l.key && selectedCard === "club", l.disabled)}
                                    >
                                        {l.label}{stateSuffix(l.state)}
                                    </button>
                                ))}
                            </div>

                            {/* iter87.592 — "Details" collapsed into this League step
                                (no real further choice once a league is picked); scoring
                                info moved up to the League header above. */}
                            {selectedCard === "club" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2.5" data-testid="club-season-details">
                                    {activeHalf && (
                                        <div
                                            className="text-[11px] text-zinc-400 bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 leading-relaxed"
                                            data-testid="club-season-half-notice"
                                        >
                                            Locked to <b className="text-cyber">{activeHalf.window === "second_half" ? "2nd Half" : "1st Half"} only</b> — Matchweeks{" "}
                                            {activeHalf.window === "second_half" ? `${half + 1}–${rounds}` : `1–${half}`}.
                                            {activeHalf.window === "second_half" && " Pots are seeded from today's live table, not a preseason projection."}
                                        </div>
                                    )}
                                    <BoostNotice />
                                </div>
                            )}
                        </div>
                    )}

                    {sport === "soccer" && style === "tournament" && liveWcTournaments.length > 0 && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            {liveWcTournaments.length > 1 ? (
                                <>
                                    <StepHeader n={3} label="Tournament" />
                                    <select
                                        data-testid="soccer-tournament-select"
                                        value={wcEntry?.key || ""}
                                        onChange={(e) => {
                                            const t = liveWcTournaments.find((x) => x.key === e.target.value);
                                            if (!t) return;
                                            setWcKey(t.key);
                                            setWcType(t.types[0]?.key);
                                        }}
                                        className={selCls}
                                    >
                                        {wcTournaments.map((t) => (
                                            <option key={t.key} value={t.key} disabled={t.state !== "live" || t.types.length === 0}>
                                                {t.icon} {t.label}{t.state !== "live" ? (t.state === "closed" ? " — closed" : " — coming soon") : ""}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            ) : null}

                            <div className={liveWcTournaments.length > 1 ? "mt-3.5 pt-3.5 border-t border-white/[0.06]" : ""}>
                                <div className="flex items-center gap-1.5">
                                    <StepHeader n={liveWcTournaments.length > 1 ? 4 : 3} label="Format" />
                                    <ScoringInfo text={wcTypeEntry?.desc} />
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="soccer-format-options">
                                    {(wcEntry?.types || []).map((ty) => (
                                        <button
                                            key={ty.key}
                                            type="button"
                                            data-testid={`soccer-format-${ty.key}-btn`}
                                            onClick={() => { setWcType(ty.key); pickTournamentType(ty.key); }}
                                            className={pillCls(template === ty.key)}
                                        >
                                            {ty.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {sport === "soccer" && style === "weekly" && soccerBoostTypes.some((n) => !n.disabled) && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="League" />
                            <div className="flex flex-wrap gap-2" data-testid="soccer-boost-league-options">
                                {soccerBoostTypes.map((n) => (
                                    <button
                                        key={n.key}
                                        type="button"
                                        data-testid={`soccer-boost-league-${n.key}-btn`}
                                        disabled={n.disabled}
                                        onClick={() => pickSoccerWeekly(n.key)}
                                        className={pillCls(template === n.key, n.disabled)}
                                    >
                                        {n.label}{stateSuffix(n.state)}
                                    </button>
                                ))}
                            </div>

                            {selectedCard === "soccerWeekly" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="soccer-boost-details">
                                    <div className="flex items-center gap-1.5">
                                        <StepHeader n={4} label="Details" />
                                        <ScoringInfo text="Every matchday your crew boosts N games from the league's weekly slate: win + cover +6 · win +2 · loss that covers 0 · miss both −2. Ties count as losses. No draft, no pots — full season." />
                                    </div>
                                    <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Picks per matchday</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={25}
                                        value={nflPicksPerRound}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") { setNflPicksPerRound(""); return; }
                                            const n = parseInt(raw, 10);
                                            if (!Number.isNaN(n)) setNflPicksPerRound(n);
                                        }}
                                        onBlur={(e) => {
                                            const n = parseInt(e.target.value, 10);
                                            setNflPicksPerRound(Number.isNaN(n) || n < 1 ? 1 : Math.min(n, 25));
                                        }}
                                        data-testid="soccer-boost-picks-per-round"
                                        className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-cyber/60 focus:outline-none px-3 py-2 text-sm text-white"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* ---- Soccer · Playoffs (NWSL) — iter87.362.
                        iter87.459 (user-requested) — a real selectable
                        League tile now, same shape as Football →
                        Playoffs below, instead of descriptive copy text
                        that read like "nothing exists here yet". */}
                    {sport === "soccer" && style === "playoffs" && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="League" />
                            <div className="flex flex-wrap gap-2" data-testid="soccer-playoffs-league-options">
                                <div className="inline-flex items-center gap-1">
                                    <button
                                        type="button"
                                        data-testid="soccer-playoffs-league-nwsl_playoffs_api-btn"
                                        disabled={nwslState !== "live"}
                                        onClick={() => { if (nwslState === "live") setTemplate("nwsl_playoffs_api"); }}
                                        className={pillCls(template === "nwsl_playoffs_api", nwslState !== "live")}
                                    >
                                        NWSL{stateSuffix(nwslState)}
                                    </button>
                                    <ScoringInfo text={nwslState === "live" ? nwslPlayoffsDesc : null} />
                                </div>
                                {/* iter87.465.20 — MLS Playoffs, sibling tile. */}
                                <div className="inline-flex items-center gap-1">
                                    <button
                                        type="button"
                                        data-testid="soccer-playoffs-league-mls_playoffs_api-btn"
                                        disabled={mlsState !== "live"}
                                        onClick={() => { if (mlsState === "live") setTemplate("mls_playoffs_api"); }}
                                        className={pillCls(template === "mls_playoffs_api", mlsState !== "live")}
                                    >
                                        MLS{stateSuffix(mlsState)}
                                    </button>
                                    <ScoringInfo text={mlsState === "live" ? mlsPlayoffsDesc : null} />
                                </div>
                                {/* iter87.466.1 — UCL Playoffs, sibling tile. */}
                                <div className="inline-flex items-center gap-1">
                                    <button
                                        type="button"
                                        data-testid="soccer-playoffs-league-ucl_playoffs_api-btn"
                                        disabled={uclState !== "live"}
                                        onClick={() => { if (uclState === "live") setTemplate("ucl_playoffs_api"); }}
                                        className={pillCls(template === "ucl_playoffs_api", uclState !== "live")}
                                    >
                                        UCL{stateSuffix(uclState)}
                                    </button>
                                    <ScoringInfo text={uclState === "live" ? uclPlayoffsDesc : null} />
                                </div>
                            </div>

                            {/* iter87.592 — "Details" collapsed into this League step;
                                per-league scoring now lives on the tiles above. */}
                            {selectedCard === "soccerPlayoffs" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="soccer-playoffs-details">
                                    <p className="text-[11px] text-zinc-500" data-testid="soccer-playoffs-card-desc">
                                        Ready to create — teams and bracket auto-sync from API-Football. Edit scoring anytime in Tournament Settings.
                                    </p>
                                    {/* iter87.579 (user-reported — "boost pick... shows for
                                        football playoffs, should it for MLS too?") — MLS Conf
                                        Semis+ reuses the same Matchday Boost infra (forced on
                                        at creation, same as NFL/CFB Playoffs) under the
                                        "Playoff Predictor" name; this panel never surfaced
                                        that at all before, unlike its NFL/CFB sibling. NWSL/
                                        UCL don't use Matchday Boost — no notice for them. */}
                                    {isMlsPlayoffsTpl(template) && <BoostNotice label="Playoff Predictor" />}
                                </div>
                            )}
                        </div>
                    )}

                    {sport === "football" && style === "weekly" && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="League" />
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="football-weekly-league-options">
                                {footballWeeklyLeagues.map((l) => (
                                    <button
                                        key={l.key}
                                        type="button"
                                        data-testid={`football-weekly-league-${l.key}-btn`}
                                        disabled={l.disabled}
                                        onClick={() => pickFootballWeekly(l.key)}
                                        className={pillCls(footballWeeklySelected === l.key, l.disabled)}
                                    >
                                        {l.label}{stateSuffix(l.state)}
                                    </button>
                                ))}
                            </div>

                            {selectedCard === "footballWeekly" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="football-weekly-details">
                                    <div className="flex items-center gap-1.5">
                                        <StepHeader n={4} label="Details" />
                                        <ScoringInfo text={nflWeeklyEntry?.desc} />
                                    </div>
                                    <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Picks per week</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={25}
                                        value={nflPicksPerRound}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") { setNflPicksPerRound(""); return; }
                                            const n = parseInt(raw, 10);
                                            if (!Number.isNaN(n)) setNflPicksPerRound(n);
                                        }}
                                        onBlur={(e) => {
                                            const n = parseInt(e.target.value, 10);
                                            setNflPicksPerRound(Number.isNaN(n) || n < 1 ? 1 : Math.min(n, 25));
                                        }}
                                        data-testid="nfl-picks-per-round"
                                        className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-cyber/60 focus:outline-none px-3 py-2 text-sm text-white"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {sport === "football" && style === "pot" && selectedCard === "footballPot" && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="football-pot-details">
                            <p className="text-[11px] text-zinc-500">League: <b className="text-zinc-300">NFL</b> — the only option live today.</p>
                            <BoostNotice />
                        </div>
                    )}

                    {sport === "football" && style === "playoffs" && (                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="League" />
                            <div className="flex flex-wrap gap-2" data-testid="football-playoffs-league-options">
                                {footballPlayoffsTypes.map((n) => (
                                    <div key={n.key} className="inline-flex items-center gap-1">
                                        <button
                                            type="button"
                                            data-testid={`football-playoffs-league-${n.key}-btn`}
                                            disabled={n.disabled}
                                            onClick={() => pickFootballPlayoffs(n.key)}
                                            className={pillCls(template === n.key, n.disabled)}
                                        >
                                            {n.label}{stateSuffix(n.state)}
                                        </button>
                                        <ScoringInfo text={n.disabled ? null : n.desc} />
                                    </div>
                                ))}
                            </div>

                            {/* iter87.592 — "Details" collapsed into this League step;
                                per-league (NFL vs CFB) scoring now lives on the tiles
                                above instead of a shared popover. */}
                            {selectedCard === "footballPlayoffs" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="football-playoffs-details">
                                    <p className="text-[11px] text-zinc-500" data-testid="football-playoffs-card-desc">
                                        Ready to create — teams and bracket auto-sync from Highlightly. Edit scoring anytime in Tournament Settings.
                                    </p>
                                    {/* iter87.579 (user-reported — "boost pick for playoff
                                        football... should be removed, not sure why it's
                                        showing") — it's real (matchday_boost.enabled is
                                        forced True at creation, powers the real in-season
                                        Playoff Predictor sidecar for Conf Semis+), the
                                        generic "Boosts" label just disagreed with the name
                                        used everywhere else for this exact mechanic. */}
                                    <BoostNotice label="Playoff Predictor" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ---- Squares (Mode → League → Details) — iter87.553, its
                 own top-level sport, Pro+/comp-only, curated game list
                 only (no free-text search, attached post-creation). ---- */}
            {sport === "squares" && (
                <div className={sectionCls} data-testid="step-style">
                    <StepHeader n={2} label="Mode" />
                    <p className="text-[11px] text-zinc-500 mb-2">
                        Pro+/comp rivalries only.{!proPlusOk && " Upgrade this rivalry to create a Squares board."}
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="squares-mode-options">
                        {SQUARES_MODES.map((m) => (
                            <button
                                key={m.key}
                                type="button"
                                data-testid={`style-squares-${m.key}-btn`}
                                disabled={!proPlusOk}
                                onClick={() => handleStylePick(m.key)}
                                className={pillCls(style === m.key, !proPlusOk)}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    {style && SQUARES_MODES.some((m) => m.key === style) && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="League" />
                            <div className="flex flex-wrap gap-2" data-testid="squares-league-options">
                                {squaresLeagueTypes.map((n) => (
                                    <button
                                        key={n.key}
                                        type="button"
                                        data-testid={`squares-league-${n.key}-btn`}
                                        disabled={n.disabled || !proPlusOk}
                                        onClick={() => pickSquaresLeague(n.key)}
                                        className={pillCls(template === n.key, n.disabled || !proPlusOk)}
                                    >
                                        {n.label}{stateSuffix(n.state)}
                                    </button>
                                ))}
                            </div>

                            {selectedCard === "squares" && (
                                <div className="mt-3.5 pt-3.5 border-t border-white/[0.06] space-y-2" data-testid="squares-details">
                                    <div className="flex items-center gap-1.5">
                                        <StepHeader n={4} label="Pick the game" />
                                        <ScoringInfo text={footballSquaresTypes.find((n) => n.key === template)?.desc} />
                                    </div>
                                    {squaresGamesLoading ? (
                                        <p className="text-[11px] text-zinc-500" data-testid="squares-wizard-games-loading">Checking the vendor feed…</p>
                                    ) : squaresGames.length === 0 ? (
                                        <p className="text-[11px] text-zinc-500" data-testid="squares-wizard-games-empty">
                                            Nothing on the shortlist yet — the vendor publishes {squaresLeagueTypes.find((n) => n.key === template)?.league === "cfb" ? "CFP bowl/National Championship" : "Super Bowl / Conference Championship"} matchups closer to kickoff. Check back later.
                                        </p>
                                    ) : (
                                        <div className="max-h-56 overflow-y-auto space-y-1.5" data-testid="squares-wizard-games">
                                            {squaresGames.map((g) => (
                                                <button
                                                    key={g.vendor_game_id}
                                                    type="button"
                                                    onClick={() => setSquaresGameId(g.vendor_game_id)}
                                                    data-testid={`squares-wizard-game-${g.vendor_game_id}`}
                                                    className={`w-full text-left rounded-lg border p-2.5 text-xs transition ${
                                                        squaresGameId === g.vendor_game_id ? "border-volt bg-volt/10" : "border-zinc-700 bg-zinc-900 hover:border-volt/50"
                                                    }`}
                                                >
                                                    <div className="text-volt font-bold uppercase tracking-wide text-[10px] mb-0.5">{g.round_label || g.week}</div>
                                                    <div className="font-bold text-white">{g.away_name} @ {g.home_name}</div>
                                                    <div className="text-zinc-500 mt-0.5">{g.scheduled_at ? new Date(g.scheduled_at).toLocaleString() : "Date TBD"}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-[11px] text-zinc-500" data-testid="squares-card-desc">
                                        {squaresGameId
                                            ? "Game picked — ready to create."
                                            : squaresGames.length === 0 && !squaresGamesLoading
                                                ? "Create Tournament is disabled until a game is on the shortlist — check back later."
                                                : "Pick a game above, then Create Tournament. Payout setup lives in Trophy Tokens afterward."}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ---- Golf ---- */}
            {sport === "golf" && golfTypes.some((g) => !g.disabled) && (
                <div className={sectionCls} data-testid="step-style">
                    <StepHeader n={2} label="Style / Game type" />
                    <div className="flex flex-wrap gap-2">
                        {golfTypes.map((g) => (
                            <div key={g.key} className="inline-flex items-center gap-1">
                                <button
                                    type="button"
                                    data-testid={`style-golf-${g.key}-btn`}
                                    disabled={g.disabled}
                                    onClick={() => handleStylePick(g.key)}
                                    className={pillCls(style === g.key, g.disabled)}
                                >
                                    {g.label}{stateSuffix(g.state)}
                                </button>
                                {/* iter87.592 — single-curated-event golf has no further
                                    League/Details step, so its scoring icon lives on the
                                    Style pill itself (multi-event golf keeps it in Details
                                    below, unchanged, since there's still a real Event
                                    choice to make first). */}
                                {!golfMultiEvent && !g.disabled && <ScoringInfo text={g.desc} />}
                            </div>
                        ))}
                    </div>

                    {style && !golfTypes.find((g) => g.key === style)?.disabled && golfMultiEvent && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <StepHeader n={3} label="Event" />
                            <select
                                data-testid="golf-event-select"
                                value={golfBindingId || ""}
                                onChange={(e) => pickGolf(style, e.target.value || null)}
                                className={selCls}
                            >
                                {golfEventOptions.map((b) => (
                                    <option key={b.id} value={b.builtin ? "" : b.id}>
                                        {b.label}{b.season ? ` — ${b.season}` : ""}{b.is_default ? " (default)" : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedCard === "golf" && golfMultiEvent && (
                        <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                            <div className="flex items-center gap-1.5">
                                <StepHeader n={4} label="Details" />
                                <ScoringInfo text={golfTypeEntry?.desc} />
                            </div>
                            <p className="text-[11px] text-zinc-500" data-testid="golf-card-desc">Ready to create — edit scoring anytime in Tournament Settings.</p>
                        </div>
                    )}
                    {/* iter87.592 — single-event golf: "Details" collapsed, scoring
                        icon moved to the Style pill above; the event is still
                        implicitly "picked" (the one curated/builtin binding) the
                        moment the Style pill is clicked — same auto-resolve as
                        before, nothing silently skipped. */}
                    {selectedCard === "golf" && !golfMultiEvent && (
                        <p className="text-[11px] text-zinc-500 mt-3" data-testid="golf-card-desc">Ready to create — edit scoring anytime in Tournament Settings.</p>
                    )}
                </div>
            )}

            {/* ---- Custom ---- */}
            {sport === "custom" && customEnabled && (
                <div className={sectionCls}>
                    <p className="font-bold flex items-center gap-2" data-testid="template-custom-btn"><span className="text-xl">🛠️</span> Custom</p>
                    <p className="text-[11px] text-zinc-500 leading-snug mt-2">Build any format from scratch: groups + knockout, knockout-only, or round-robin — manual entry or a data feed. Configure below.</p>
                </div>
            )}

            {/* ---- Baseball (MLB Playoffs) — one preset, no Style step.
                iter87.459 (user-requested) — rendered as a real
                selectable League tile (same shape as the NFL/CFB
                Playoffs league pills), not a plain header + sub-copy. */}
            {sport === "baseball" && (
                <div className={sectionCls} data-testid="baseball-details">
                    <div className="flex items-center gap-1.5">
                        <StepHeader n={2} label="League" />
                        <ScoringInfo text="6-team pick pot (no draft, 3 seed tranches) scored via the standard bracket-advancement ladder, plus Champions Pick (AL/NL/WS flat bonuses) and Series Predictor (team-to-advance + exact deciding game)." />
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="baseball-league-options">
                        <button
                            type="button"
                            data-testid="baseball-league-mlb_playoffs_api-btn"
                            onClick={() => setTemplate("mlb_playoffs_api")}
                            className={`${pillCls(template === "mlb_playoffs_api")} flex items-center gap-1.5`}
                        >
                            <SportHeaderIcon sport="baseball" className={`w-4 h-4 ${SPORT_TEXT_CLASS.heat}`} /> MLB Playoffs
                        </button>
                    </div>
                </div>
            )}

            {/* ---- Basketball (NBA Playoffs) — sibling of Baseball above,
                same Highlightly/RapidAPI vendor + card shape. Was missing
                entirely (only the sport tile existed), so picking
                Basketball dead-ended with no details card or Create path. */}
            {sport === "basketball" && (
                <div className={sectionCls} data-testid="basketball-details">
                    <div className="flex items-center gap-1.5">
                        <p className="font-bold flex items-center gap-2">
                            <SportHeaderIcon sport="basketball" className={`w-5 h-5 ${SPORT_TEXT_CLASS.blaze}`} /> NBA Playoffs
                        </p>
                        <ScoringInfo text="8-team pick pot (no draft, 3 seed tranches) scored via the standard bracket-advancement ladder, plus Champions Pick (East/West/Finals flat bonuses) and Series Predictor (team-to-advance + exact deciding game)." />
                    </div>
                    <p className="text-[11px] text-zinc-500 leading-snug mt-2" data-testid="basketball-card-desc">
                        Ready to create — teams and bracket auto-sync from Highlightly. Edit scoring anytime in Tournament Settings.
                    </p>
                </div>
            )}
        </div>
    );
}
